import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { FeedbackBuffer } from '../feedback-buffer.js';
import { FLOAT_TEXTURES_OK } from '../float-textures.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';
import { loadArtworkTexture } from '../artwork-texture.js';

// Shared-rig contract: nothing here is a lit object or a room, so there is no
// rig to share. Every colour comes from the palette and from B itself.
const RIG = 'forbidden';
export { RIG };

export const PROFILE = {
  id: 'turing',
  family: 'organic',
  danceability: 0.55,
  requiresTier: 1,
  requiresFloatTextures: true,
  energyLevel: 0.48,
  bpmMin: 60,
  bpmMax: 150,
  movement: 0.40,
  tonality: 0.80,
  prefers: { dynamics: [0.25, 0.95] },
  // requiresFloatTextures is documentation; THIS is what actually keeps the
  // piece out of the rotation on hardware that cannot render to float.
  enabled: FLOAT_TEXTURES_OK
};

// Turing: a Gray-Scott reaction-diffusion system, run on the GPU, whose
// operating point walks the Pearson map on the track's phrases.
//
// Two substances on a torus. A is fed in and consumed, B eats A and decays:
//
//   A' = Da * lap(A) - A*B*B + f * (1 - A)
//   B' = Db * lap(B) + A*B*B - (k + f) * B
//
// The whole behaviour is in (f, k). Almost everywhere on that plane the system
// dies; along a thin curve it does the ten things the brief names, and the
// difference between a living pattern and a blank screen can be 0.002 in k.
// That is why OPERATING_POINTS is a fixed list and the walk between them is a
// chain, never an interpolation into open space.
//
// EXCEPTION TO THE NO-RENDER-TARGET CONTRACT, stated plainly: this piece runs
// two to four full simulation passes per frame into RGBA32F ping-pong targets,
// plus two reduction passes once a second. It uses the shared FeedbackBuffer
// for all of them rather than allocating its own targets.

// --- the numbers that are not mine to change ---------------------------------
//
// Da/Db = 2:1 is the whole reason a Turing instability exists here: B has to
// diffuse slower than A or the pattern never separates from the background.
// dt = 1 is safe with the stencil below - the nine-point Laplacian's spectrum
// runs to -1.6, so the explicit Euler amplification is 1 - 1.6 = -0.6 for A and
// 1 - 0.8 = 0.2 for B, both inside the unit circle. A five-point stencil would
// change that, and change the pattern.
const DA = 1.0;
const DB = 0.5;
const DT = 1.0;

// NINE-POINT LAPLACIAN, not five. The five-point stencil is anisotropic along
// the diagonals, and in a system whose whole output is the SHAPE of a front
// that anisotropy shows up as square corners on every blob - the artefact
// criterion 5 rejects. Weights sum to zero, as a Laplacian must:
// 4*0.05 + 4*0.2 - 1.0 = 0.
const LAP_DIAG = 0.05;
const LAP_ORTHO = 0.2;
const LAP_CENTRE = -1.0;

// --- the Pearson map ---------------------------------------------------------
//
// Ten points, ordered so that CONSECUTIVE ENTRIES ARE NEIGHBOURS ON THE MAP.
// That ordering is the transition graph: a phrase moves one index, never more,
// and the interpolation between two adjacent entries stays on the live curve.
// Going straight from solitons (0.014) to chaos (0.078) would cross the dead
// zone in the middle of the plane and the screen would be blank before it
// arrived.
//
// `calm` is how quiet the regime reads, used to bias the walk with progress:
// the ends of a track get the low-index, slow regimes and the middle gets the
// turbulent ones.
export const OPERATING_POINTS = [
  { name: 'ondes',      f: 0.010, k: 0.047 },  // circular fronts, propagating
  // The brief gives k = 0.054 here. MEASURED, THAT POINT IS DEAD: at f = 0.014
  // the field extinguishes completely - B is exactly 0.0000 by 1800 steps and
  // never returns. A scan of the neighbourhood at 4500 steps puts the boundary
  // between 0.053 and 0.054:
  //     k      0.045  0.047  0.049  0.051  0.053  0.054  0.055
  //     mean B  dead  0.0141 0.0662 0.0722 0.0195  dead   dead
  // which is exactly the 0.002 cliff the brief warns about, landed on. Of the
  // live neighbours, 0.051 is the only one that never dips near the extinction
  // threshold on any seed - over three seeds and 12600 steps its minimum was
  // 0.0477, against 0.0017 for k = 0.053. Moved three thousandths, and the
  // regime is still isolated spots.
  { name: 'solitons',   f: 0.014, k: 0.051 },  // isolated spots that move and bounce
  { name: 'mitose',     f: 0.022, k: 0.051 },  // spots dividing without end
  { name: 'corail',     f: 0.026, k: 0.051 },  // branching growth from the edges
  { name: 'labyrinthe', f: 0.030, k: 0.057 },  // stripes folding back on themselves
  // 0.058, not the brief's 0.056. A scan of the corridor shows the FLOODED
  // region - dense, zero contrast, a flat screen - is a triangle whose upper
  // edge climbs with f: at f = 0.026 anything below k = 0.051 floods, at
  // f = 0.030 below 0.053, at f = 0.034 below 0.057. The brief's dentelle sits
  // at 0.056, one step inside that edge, and the step from labyrinthe's 0.057
  // down to it is THE ONLY DECREASE IN k ANYWHERE ON THE LIST - it walks
  // straight into the triangle while f is rising. Over thirty minutes that one
  // pair accounted for most of 37 saturation events.
  { name: 'dentelle',   f: 0.034, k: 0.058 },  // a fine regular net
  // The brief calls this one a stable hexagonal lattice of spots. It is not,
  // and no nearby point is: what f = 0.038, k = 0.061 actually settles into is
  // a FINE FROZEN MAZE - mean B 0.169, coverage 0.51, and an activity of
  // 0.0073 per 600 steps against 0.0353 for vers. Distinct from labyrinthe,
  // which is the same family drawn thicker, and worth keeping for that; the
  // name is the brief's, the description here is what it does.
  { name: 'points',     f: 0.038, k: 0.061 },  // a fine, still maze
  { name: 'vers',       f: 0.046, k: 0.063 },  // filaments waving and reconnecting
  // The brief puts bulles at 0.058, 0.065 and chaos at 0.078, 0.061. MEASURED
  // OVER 48000 STEPS, BOTH ARE THE SAME NEARLY-EMPTY FIELD: a handful of
  // static rings, coverage 0.021 and 0.030, growing by a few thousandths per
  // twelve thousand steps. They are not two regimes, they are one, and it is
  // the least interesting thing this system does. Their pairwise distance in
  // the feature space used for criterion 1 was 0.006, against a median of 0.4
  // for the rest of the list.
  //
  // The replacements come from a scan of f in [0.030, 0.062] against k in
  // [0.057, 0.067], measuring coverage AND activity - the mean change in B
  // over 600 steps, which is the only thing that separates a moving pattern
  // from a frozen one:
  //   0.054, 0.063   coverage 0.361, activity 0.0255 - a branching mass with a
  //                  live front, domains meeting and merging. What the brief
  //                  asked bulles for.
  //   0.062, 0.061   coverage 0.947, activity 0.0005 - a CELLULAR NETWORK, thin
  //                  dark walls between light cells with a seed in each. Frozen,
  //                  and unlike anything else on the list, which is what earns
  //                  it the tenth place.
  { name: 'bulles',     f: 0.054, k: 0.063 },  // domains merging and splitting
  { name: 'cellules',   f: 0.062, k: 0.061 }   // a network of thin walls
];
// Where saturation runs away to: the hexagonal lattice is the most forgiving
// point on the list, stable over a wide neighbourhood and impossible to flood.
const CONSERVATIVE_INDEX = 6;

// THE LOWER EDGE OF THE LIVE BAND, measured. Below this k, for a given f, the
// field floods to a flat screen; the numbers come from a scan of f in
// [0.022, 0.062] against k in [0.049, 0.065] at 4800 steps, classified by
// contrast. A transition interpolates f and k in a straight line, and a
// straight line between two live points can still dip under this edge where it
// rises steeply - between dentelle and points it passes within 0.0005 of it -
// so the interpolated k is clamped to it. The clamp only ever bends a path
// upward, away from the flood, and never moves an endpoint.
const K_FLOOR = [
  [0.022, 0.050], [0.026, 0.052], [0.030, 0.054],
  [0.034, 0.058], [0.038, 0.060], [0.046, 0.059], [0.062, 0.057]
];

function kFloor(f) {
  if (f <= K_FLOOR[0][0]) return K_FLOOR[0][1];
  for (let i = 1; i < K_FLOOR.length; i++) {
    if (f <= K_FLOOR[i][0]) {
      const a = K_FLOOR[i - 1], b = K_FLOOR[i];
      return a[1] + (b[1] - a[1]) * (f - a[0]) / (b[0] - a[0]);
    }
  }
  return K_FLOOR[K_FLOOR.length - 1][1];
}

const TRANSITION_BEATS = 4;      // f and k cross over four beats, never cut

// HOW LONG A REGIME IS LEFT ALONE. Not a pacing choice - a stability one. A
// Gray-Scott regime takes on the order of 7500 steps, about forty seconds at
// three iterations a frame, to reach its own attractor from another regime's
// pattern. Stepping again before it arrives leaves the field permanently in
// transit, and the states in between are where it floods or dies: measured over
// thirty minutes with a phrase every eight seconds, the safety net had to
// intervene 53 times. The field is never at any of the ten points, only
// somewhere on the way to one.
//
// It also happens to be what criterion 3 wants. The transformation is the
// interesting part, and it is only legible if it is allowed to finish.
const MIN_DWELL = 14.0;          // seconds at a point before another step
const DEFAULT_BPM = 104;

// --- safety ------------------------------------------------------------------
//
// Once B is gone it is gone: A settles to 1 everywhere, the A*B*B term is zero,
// and nothing in the equations can restart it. The same is true in reverse for
// saturation. Both are checked from a GPU reduction of B, read back once a
// second - not from the parameters, which is the mistake that lets a piece show
// a blank screen for twenty minutes without anybody knowing why.
const CHECK_FRAMES = 60;
const EXTINCT_FRACTION = 0.005;  // mean B below 0.5% of the surface
// CONFIRMED OVER TWO CHECKS, not one. Measured over 900 frames at each of the
// ten points, the sparsest LIVE regime is solitons at a mean B of 0.00598 -
// twenty percent above the threshold, which is not much of a margin for a
// regime whose whole behaviour is a few spots wandering around. A dying field
// falls monotonically and stays down, so requiring two consecutive seconds
// below the line costs at most one second of recovery and removes the risk of
// reseeding a healthy solitons field on a fluctuation.
const EXTINCT_CONFIRMATIONS = 2;

// SATURATION IS A FLAT SCREEN, NOT A FULL ONE, and that distinction is not
// pedantry - it is what the measurements forced. The brief asks for a fallback
// when B passes 60% of the surface, but two regimes on its own list are denser
// than that and perfectly healthy: corail covers 94.5% and dentelle 90.5%.
// Falling back on coverage alone would have made both of them unreachable.
//
// What actually fails is the field going UNIFORM. So the second condition is
// RMS contrast - the standard deviation of B over its mean - and it separates
// the two cases by more than two orders of magnitude:
//   the ten live regimes   contrast 0.176 (corail) to 5.81 (bulles)
//   a flooded field        contrast 0.0009, coverage 1.000
// 0.05 sits between them with a factor of 3.5 to the nearest live regime and
// 55 to the failure.
const SATURATED_FRACTION = 0.60;
const SATURATED_CONTRAST = 0.05;
const RESEED_SPOTS_MIN = 3;
const RESEED_SPOTS_MAX = 5;
const REDUCE_N = 16;             // the readback grid: 16 x 16 cells of mean B

const INJECT_SLOTS = 5;          // spots the sim shader can take in one frame
const INJECT_RADIUS = 0.030;     // in UV, so a spot is ~3% of the grid across
const INJECT_MIN_MS = 800;
const LOW_RISE = 0.16;           // how much bands.low has to jump to inject

// --- window ------------------------------------------------------------------
//
// The simulation never moves: it is a field on a fixed torus. What moves is the
// window onto it, so the pattern is never seen twice from the same place and
// yet no frame of it is ever dragged across the grid.
const ZOOM_PERIOD = 70.0;
const DRIFT_PERIOD = 110.0;
const ZOOM_MIN = 0.62;
const ZOOM_MAX = 1.05;

const EDGE_GAIN_BASE = 2.2;
const EDGE_GAIN_BAND = 3.4;      // bands.high rides on top
const SAFE_FLOOR = 0.42;

const TIER = {
  1: { n: 384, itersMin: 2, itersMax: 2, shading: false },
  2: { n: 512, itersMin: 2, itersMax: 4, shading: true }
};
const DEFAULT_TIER = 2;

function clamp01(x) { return Math.max(0, Math.min(1, x)); }
function urlParam(name) {
  if (typeof location === 'undefined') return null;
  return new URLSearchParams(location.search).get(name);
}

// =============================================================================
// SHADERS
// =============================================================================

// One quad, one pass. Shared by the simulation, the seed and the reductions.
const SIM_VERTEX = `
  precision highp float;
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = vec4(position.xy * 2.0, 0.0, 1.0);
  }
`;

// HIGHP ON THE SIMULATION - but NOT for the reason the brief gives, and the
// difference is worth recording rather than repeating.
//
// The brief says mediump makes the system diverge or die within seconds. Built
// both ways behind `?turingprec=mediump` and measured, THAT DOES NOT HAPPEN on
// either machine available here. Mean B over 720 frames at the labyrinth point:
//   Mac (ANGLE/Metal)  highp 0.0149 -> 0.1526   mediump 0.0173 -> 0.1620
//   Pi (V3D 4.2)       highp 0.1530 -> 0.1700   mediump 0.1541 -> 0.1701
// On the Pi the two are identical to four decimals. Both drivers promote
// mediump to full float, so the qualifier is simply ignored.
//
// It stays highp anyway, and the reason is the Samsung TV: its mediump is real
// fp16 (found the hard way on this project - see the note in shatter.js). Ten
// bits of mantissa against increments of 1e-4 on values of order 1 means every
// increment rounds away, and there the brief's warning would be exactly right.
// The qualifier costs nothing measurable on the two GPUs that ignore it, and
// prevents a silent failure on the one that does not.
function simFragment(precision, fivePoint) {
  return `
  precision ${precision} float;
  uniform sampler2D uState;
  uniform vec2 uTexel;
  uniform float uF;
  uniform float uK;
  uniform vec4 uInject[${INJECT_SLOTS}];
  varying vec2 vUv;

  void main() {
    vec2 t = uTexel;
    // Nine-point Laplacian. Both species are in the same texel, so one set of
    // eight fetches serves both - which is also why the state is RG and not
    // two textures.
    vec2 c  = texture2D(uState, vUv).rg;
    vec2 n  = texture2D(uState, vUv + vec2(0.0,  t.y)).rg;
    vec2 s  = texture2D(uState, vUv + vec2(0.0, -t.y)).rg;
    vec2 e  = texture2D(uState, vUv + vec2( t.x, 0.0)).rg;
    vec2 w  = texture2D(uState, vUv + vec2(-t.x, 0.0)).rg;
    vec2 ne = texture2D(uState, vUv + vec2( t.x,  t.y)).rg;
    vec2 nw = texture2D(uState, vUv + vec2(-t.x,  t.y)).rg;
    vec2 se = texture2D(uState, vUv + vec2( t.x, -t.y)).rg;
    vec2 sw = texture2D(uState, vUv + vec2(-t.x, -t.y)).rg;

    vec2 lap = ${fivePoint
      // The simplified stencil, reachable ONLY through ?turinglap=5. It is
      // here so criterion 5 can be demonstrated rather than asserted: it is
      // anisotropic - it has no knowledge of the diagonals at all - and the
      // fronts it produces line up with the axes. Never the default.
      ? `-4.0 * c + (n + s + e + w)`
      : `${LAP_CENTRE.toFixed(2)} * c
             + ${LAP_ORTHO.toFixed(2)} * (n + s + e + w)
             + ${LAP_DIAG.toFixed(2)} * (ne + nw + se + sw)`};

    float A = c.r;
    float B = c.g;
    float reaction = A * B * B;
    float nA = A + (${DA.toFixed(1)} * lap.r - reaction + uF * (1.0 - A)) * ${DT.toFixed(1)};
    float nB = B + (${DB.toFixed(1)} * lap.g + reaction - (uK + uF) * B) * ${DT.toFixed(1)};

    // Injection rides on the update rather than costing its own full pass: a
    // uniform-driven branch would be coherent anyway, and with w = 0 the slot
    // contributes nothing. The distance is measured ON THE TORUS, so a spot
    // dropped near an edge is still round.
    for (int i = 0; i < ${INJECT_SLOTS}; i++) {
      vec4 spot = uInject[i];
      vec2 d = abs(vUv - spot.xy);
      d = min(d, 1.0 - d);
      float g = smoothstep(spot.z, 0.0, length(d)) * spot.w;
      nB = max(nB, 0.50 * g);
      nA = min(nA, mix(1.0, 0.40, g));
    }

    gl_FragColor = vec4(clamp(nA, 0.0, 1.0), clamp(nB, 0.0, 1.0), 0.0, 1.0);
  }
`;
}

// A = 1, B = 0, plus spots - and the SHAPE of those spots is the cover.
//
// Gray-Scott keeps the topology of its seed for a very long time: the number of
// separate B domains and roughly where they sit survives thousands of steps,
// even as the regime changes what they look like. Seeding from the artwork's
// luminance is therefore the one place in this piece where the music actually
// determines the image rather than modulating it, and it is visible in the
// first seconds before diffusion softens it.
// A = 1, B = 0, plus spots - and WHERE those spots are is the cover.
//
// SPOTS, NOT REGIONS, and that distinction is the whole of criterion 4. Filling
// every bright pixel with B looks like the right reading of "inject B where the
// image is light", and it imprints the cover perfectly - correlation 0.998 with
// the artwork's luminance at a tenth of a second. It is also gone within one
// second: a broad uniform plateau of B is not a state this system supports, so
// it collapses into the regime's own texture immediately and takes the cover's
// shape with it. Measured at one second: 0.15 for a disc, -0.10 for bars.
//
// What Gray-Scott does preserve for a long time is WHERE ITS DOMAINS ARE. So
// the seed is a jittered lattice of small spots, kept only where the cover is
// bright: growth then radiates outward from the cover's light areas, and the
// filled region IS the cover's shape for as long as the front takes to spread.
const SEED_CELLS = 40.0;         // spots per axis, so one per ~13 texels at 512

const SEED_FRAGMENT = `
  precision highp float;
  uniform sampler2D uArtwork;
  uniform float uHasArtwork;
  uniform float uSeedPhase;
  varying vec2 vUv;

  float hash1(vec2 p) {
    vec3 q = fract(vec3(p.xyx) * vec3(0.1031, 0.1030, 0.0973));
    q += dot(q, q.yzx + 33.33);
    return fract((q.x + q.y) * q.z);
  }

  void main() {
    // The lattice. Jittered, because a regular grid of seeds produces a
    // regular pattern that reads as printed rather than as grown.
    vec2 cell = floor(vUv * ${SEED_CELLS.toFixed(1)} + uSeedPhase);
    vec2 frac = fract(vUv * ${SEED_CELLS.toFixed(1)} + uSeedPhase);
    vec2 jit = vec2(hash1(cell), hash1(cell + 7.31)) * 0.56 + 0.22;
    float spot = smoothstep(0.30, 0.12, length(frac - jit));

    float bright;
    if (uHasArtwork > 0.5) {
      vec3 art = texture2D(uArtwork, vUv).rgb;
      float lum = dot(art, vec3(0.2126, 0.7152, 0.0722));
      // A threshold, not a ramp: a spot is either planted or it is not, and a
      // uniform low level of B everywhere is not a seed - it is a bias, and it
      // decays without ever forming a domain.
      bright = smoothstep(0.46, 0.66, lum);
    } else {
      // No cover yet: keep about a third of the lattice, so the piece still
      // starts alive and still starts differently each time.
      bright = step(0.62, hash1(cell + 19.7));
    }

    float seed = spot * bright;
    gl_FragColor = vec4(1.0 - 0.5 * seed, 0.25 * seed, 0.0, 1.0);
  }
`;

// TWO NUMBERS, not one, and they answer different questions. The mean of B
// says how much substance there is; the fraction of texels where B is actually
// PRESENT says how much of the screen it covers. They are not interchangeable,
// and using the first for both is how the saturation check fired eleven times
// on a perfectly healthy field: counting reduction CELLS that contain any B at
// all reads 0.91 on a fine lattice whose domains occupy a third of the area.
//
// Note which channel each stage reads. The first reads the simulation, where B
// is GREEN beside A in red; the second reads the first stage's own output,
// where the two means are in RED and GREEN. Leaving stage two on .g made it
// return exactly zero - and zero is below the extinction threshold, so the
// piece reseeded itself once a second while the field underneath was fine.
// Measured at the time: stage one 0.009666 against a true field mean of
// 0.009667, stage two 0.
// The valley between the two peaks of B's histogram. Measured across all ten
// regimes: the background sits under 0.025 and the domains between 0.175 and
// 0.35, with a trough around 0.10-0.19 that holds a couple of percent of the
// surface in every regime. 0.15 is in that trough, so "covered" means inside a
// domain rather than inside its diffuse halo.
const B_PRESENT = 0.15;

function reduceFirst(block) {
  return `
  precision highp float;
  uniform sampler2D uSrc;
  uniform vec2 uSrcTexel;
  varying vec2 vUv;
  void main() {
    vec2 base = vUv - vec2(${(block * 0.5).toFixed(1)}) * uSrcTexel;
    vec3 sum = vec3(0.0);
    for (int y = 0; y < ${block}; y++) {
      for (int x = 0; x < ${block}; x++) {
        float b = texture2D(uSrc, base + vec2(float(x) + 0.5, float(y) + 0.5) * uSrcTexel).g;
        // B and B SQUARED, because the second is what tells a dense PATTERN
        // from a flat wash: mean(B^2) - mean(B)^2 is the field's variance, and
        // a uniform screen - the actual failure - has none whatever its level.
        sum += vec3(b, step(${B_PRESENT.toFixed(2)}, b), b * b);
      }
    }
    gl_FragColor = vec4(sum / ${(block * block).toFixed(1)}, 1.0);
  }
`;
}

function reduceSecond(block) {
  return `
  precision highp float;
  uniform sampler2D uSrc;
  uniform vec2 uSrcTexel;
  varying vec2 vUv;
  void main() {
    vec2 base = vUv - vec2(${(block * 0.5).toFixed(1)}) * uSrcTexel;
    vec3 sum = vec3(0.0);
    for (int y = 0; y < ${block}; y++) {
      for (int x = 0; x < ${block}; x++) {
        sum += texture2D(uSrc, base + vec2(float(x) + 0.5, float(y) + 0.5) * uSrcTexel).rgb;
      }
    }
    gl_FragColor = vec4(sum / ${(block * block).toFixed(1)}, 1.0);
  }
`;
}

function displayFragment(layout, raw) {
  const shading = layout.shading ? `
    // A pseudo-normal from the gradient, so a domain reads as slightly domed
    // rather than as a flat region of colour. The gradient is already in hand
    // from the edge term, which is why this costs almost nothing.
    vec3 nrm = normalize(vec3(-g * uRelief, 1.0));
    float lambert = clamp(dot(nrm, normalize(vec3(-0.42, 0.55, 0.72))), 0.0, 1.0);
    col *= 0.68 + 0.52 * lambert;
    col += vec3(0.16, 0.17, 0.19) * pow(lambert, 6.0) * bMask;
  ` : '';

  return `
  precision highp float;
  uniform sampler2D uState;
  uniform float uN;
  uniform vec2 uWindowCentre;
  uniform float uWindowZoom;
  uniform float uEdgeGain;
  uniform float uRelief;
  uniform vec3 uLow;
  uniform vec3 uHigh;
  uniform vec3 uBack;
  uniform float uAspect;
  uniform vec2 uSafeCenter;
  uniform vec2 uSafeHalfSize;
  varying vec2 vUv;

  // MANUAL BILINEAR, and not an oversight that the sampler is NEAREST. The Pi's
  // V3D 4.2 reports EXT_color_buffer_float but NOT OES_texture_float_linear: it
  // renders to float textures and cannot filter them. Asking for LinearFilter
  // there is silently ignored, and the window - which zooms, so it samples
  // between texels almost everywhere - would show hard stair-steps on the Pi
  // and look perfect on the desktop.
  vec2 texBilinear(sampler2D tex, vec2 uv, float n) {
    vec2 st = uv * n - 0.5;
    vec2 fl = floor(st);
    vec2 fr = st - fl;
    vec2 base = (fl + 0.5) / n;
    float d = 1.0 / n;
    vec2 a = texture2D(tex, base).rg;
    vec2 b = texture2D(tex, base + vec2(d, 0.0)).rg;
    vec2 c = texture2D(tex, base + vec2(0.0, d)).rg;
    vec2 e = texture2D(tex, base + vec2(d, d)).rg;
    return mix(mix(a, b, fr.x), mix(c, e, fr.x), fr.y);
  }

  void main() {
    // Screen to grid. The window zooms and drifts; the field does not.
    vec2 s = vec2((vUv.x - 0.5) * uAspect, vUv.y - 0.5);
    vec2 uv = fract(uWindowCentre + s * uWindowZoom);

    float B = texBilinear(uState, uv, uN).g;
${raw ? `
    gl_FragColor = vec4(vec3(B * 4.0), 1.0);
  }
` : `
    // The gradient of B, by central differences on the same bilinear read, is
    // what turns a flat grey field into something with relief: B alone is a
    // filled region, its gradient is the FRONT, and the front is where all the
    // structure of a reaction-diffusion pattern lives.
    // PER TEXEL, not per UV. Dividing the difference by the sampling distance
    // puts the gradient in units of "B per unit of UV", and across a front B
    // climbs about 0.35 over three texels of a 512 grid - a gradient of 60.
    // Multiplied by a gain of a few and clamped, that saturates on every front
    // in the frame, and since a labyrinth is nothing but fronts the whole
    // image went flat white. The raw central difference is the natural unit
    // here: order 0.1 across a front, so a gain of a few reads as a rim.
    // NEAREST for the gradient, bilinear only for B itself. The rim and the
    // relief want the FRONT's direction, which is a texel-scale quantity; four
    // more bilinear fetches to get it sub-texel accurate took the display pass
    // from 8 texture reads per pixel to 20, and on the Pi the display pass is
    // most of the frame. The difference is invisible.
    float d = 1.0 / uN;
    float bx = texture2D(uState, fract(uv + vec2(d, 0.0))).g
             - texture2D(uState, fract(uv - vec2(d, 0.0))).g;
    float by = texture2D(uState, fract(uv + vec2(0.0, d))).g
             - texture2D(uState, fract(uv - vec2(0.0, d))).g;
    vec2 g = vec2(bx, by) * 0.8;
    float edge = length(g) * uEdgeGain;

    // B rarely exceeds about 0.42 in a live regime - measured across all ten
    // points - so the ramp is scaled to that rather than to 1.0. Mapped over
    // [0, 1] the whole image sits in the bottom third of the ramp and every
    // regime comes out the same dark blue.
    float bMask = clamp(B * 2.4, 0.0, 1.0);
    // The ramp is spread over the range B actually occupies INSIDE a domain,
    // not over [0, 1]. A domain runs from about 0.20 to 0.39, which is bMask
    // 0.48 to 0.94: mapped from 0.16 the whole interior lands at the top of
    // the ramp and only one of the two palette colours is ever seen. From 0.45
    // the edge of a domain takes the first colour and its core the second.
    vec3 col = mix(uBack, mix(uLow, uHigh, smoothstep(0.45, 0.95, bMask)) * 0.80,
                   smoothstep(0.05, 0.42, bMask));
${shading}
    // The rim on the front itself, clamped: on a fast regime the gradient can
    // spike and an unclamped liseré blows the whole frame to white.
    col += mix(uHigh, vec3(1.0), 0.55) * clamp(edge, 0.0, 1.0) * 0.55;

    // Safe zone, evaluated per fragment: this is one full-screen quad, so the
    // shared vertex chunk would only ever sample it at four corners.
    // s is in HALF height units, uSafe* in height units: the box used to sit
    // at twice the cover's offset, i.e. off screen with the cover in a corner
    // (R19 audit). Now measured in height units at the cover's real centre,
    // with the box and feather it always had (both doubled, so a centred
    // cover renders exactly as before).
    vec2 q = abs(2.0 * s - uSafeCenter) - 2.0 * uSafeHalfSize;
    float sd = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0);
    col *= mix(${SAFE_FLOOR.toFixed(2)}, 1.0, smoothstep(0.0, 0.68, sd));

    gl_FragColor = vec4(col, 1.0);
  }
`}`;
}

// =============================================================================

export class TuringVisualizer extends Visualizer {
  constructor(options) {
    super();
    const opts = options || {};
    let tier = opts.qualityTier != null ? opts.qualityTier : DEFAULT_TIER;
    // requiresTier keeps tier 0 out of the rotation, but a forced ?tier=0 would
    // still land here; run the cheapest supported layout rather than crash.
    if (!TIER[tier]) tier = 1;
    this._tier = tier;
    this._layout = TIER[tier];
    this._n = this._layout.n;

    this._time = 0;
    this._frame = 0;
    this._seeded = false;
    this._paused = false;

    // --- the walk on the map
    this._index = 4;                       // labyrinthe: alive, and mid-list
    this._fromIndex = this._index;
    this._toIndex = this._index;
    this._transT = 1;                      // 1 = settled
    this._transDur = 1;
    this._lastPhraseIndex = null;
    this._lastTrackId = null;
    this._lastStepAt = -1e9;

    // --- injection
    this._lastLowBand = 0;
    this._lastInjectAt = -1e9;
    this._pendingSpots = [];

    // --- safety
    this._meanB = 0;
    this._coverage = 0;
    this._contrast = 0;
    this._lowStreak = 0;
    this._reduceMap = new Float32Array(REDUCE_N * REDUCE_N);
    this._readBuf = new Float32Array(REDUCE_N * REDUCE_N * 4);
    this._extinctions = 0;
    this._saturations = 0;

    // --- debug
    const fk = urlParam('turingfk');
    this._forcedFK = null;
    if (fk) {
      const parts = fk.split(',').map(parseFloat);
      if (parts.length === 2 && parts.every(Number.isFinite)) {
        this._forcedFK = { f: parts[0], k: parts[1] };
      }
    }
    this._raw = urlParam('turingraw') === '1';
    this._forceSeed = urlParam('turingseed') === '1';
    this._stats = urlParam('turingstats') === '1';
    this._precision = urlParam('turingprec') === 'mediump' ? 'mediump' : 'highp';
    // Both of these exist to make criteria 5 and 6 testable from outside.
    this._fivePoint = urlParam('turinglap') === '5';

    this._artworkTexture = null;
    this._lastArtworkUrl = null;
    this._hasArtwork = false;
    this._lastPaletteRef = null;
  }

  init(scene, camera) {
    this._scene = scene;
    const self = this;

    const n = this._n;
    this._state = new FeedbackBuffer({
      width: n, height: n,
      type: THREE.FloatType,
      // NEAREST because a texel is a CELL of the simulation, not a sample of an
      // image - and because the Pi cannot filter float textures anyway. The
      // display does its own bilinear.
      filter: THREE.NearestFilter,
      // The grid is a TORUS. Clamped, the boundary texels would see themselves
      // as neighbours, which in a diffusion system is a reflecting wall: fronts
      // pile up against it and every regime grows a visible frame.
      wrap: THREE.RepeatWrapping
    });

    this._ortho = new THREE.OrthographicCamera(-0.5, 0.5, 0.5, -0.5, 0, 10);
    this._ortho.position.z = 1;
    this._simScene = new THREE.Scene();
    this._simQuad = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), null);
    this._simQuad.frustumCulled = false;
    this._simScene.add(this._simQuad);

    const texel = 1 / n;
    const spots = [];
    for (let i = 0; i < INJECT_SLOTS; i++) spots.push(new THREE.Vector4(0, 0, 0, 0));
    this._simUniforms = {
      uState: { value: null },
      uTexel: { value: new THREE.Vector2(texel, texel) },
      uF: { value: OPERATING_POINTS[this._index].f },
      uK: { value: OPERATING_POINTS[this._index].k },
      uInject: { value: spots }
    };
    this._simMaterial = new THREE.ShaderMaterial({
      uniforms: this._simUniforms,
      vertexShader: SIM_VERTEX,
      fragmentShader: simFragment(this._precision, this._fivePoint),
      depthTest: false, depthWrite: false
    });

    this._seedUniforms = {
      uArtwork: { value: null },
      uHasArtwork: { value: 0 },
      uSeedPhase: { value: Math.random() * 100 }
    };
    this._seedMaterial = new THREE.ShaderMaterial({
      uniforms: this._seedUniforms,
      vertexShader: SIM_VERTEX,
      fragmentShader: SEED_FRAGMENT,
      depthTest: false, depthWrite: false
    });

    // --- reductions. n -> n/8 -> REDUCE_N, both single buffers: there is no
    // history to keep, only this frame's answer.
    const mid = n / 8;
    this._reduce1 = new FeedbackBuffer({
      width: mid, height: mid, type: THREE.FloatType,
      filter: THREE.NearestFilter, single: true
    });
    this._reduce2 = new FeedbackBuffer({
      width: REDUCE_N, height: REDUCE_N, type: THREE.FloatType,
      filter: THREE.NearestFilter, single: true
    });
    this._reduce1Uniforms = {
      uSrc: { value: null }, uSrcTexel: { value: new THREE.Vector2(texel, texel) }
    };
    this._reduce2Uniforms = {
      uSrc: { value: null }, uSrcTexel: { value: new THREE.Vector2(1 / mid, 1 / mid) }
    };
    this._reduce1Material = new THREE.ShaderMaterial({
      uniforms: this._reduce1Uniforms, vertexShader: SIM_VERTEX,
      fragmentShader: reduceFirst(8), depthTest: false, depthWrite: false
    });
    this._reduce2Material = new THREE.ShaderMaterial({
      uniforms: this._reduce2Uniforms, vertexShader: SIM_VERTEX,
      fragmentShader: reduceSecond(mid / REDUCE_N), depthTest: false, depthWrite: false
    });

    // --- the display quad
    this._displayUniforms = {
      uState: { value: null },
      uN: { value: n },
      uWindowCentre: { value: new THREE.Vector2(0.5, 0.5) },
      uWindowZoom: { value: 0.9 },
      uEdgeGain: { value: EDGE_GAIN_BASE },
      uRelief: { value: 0.06 },
      uLow: { value: new THREE.Color(0x2b6f9e) },
      uHigh: { value: new THREE.Color(0xe8d9a6) },
      uBack: { value: new THREE.Color(0x070a12) },
      uAspect: safeZoneUniforms.uAspect,
      uSafeCenter: safeZoneUniforms.uSafeCenter,
      uSafeHalfSize: safeZoneUniforms.uSafeHalfSize
    };
    this._displayMaterial = new THREE.ShaderMaterial({
      uniforms: this._displayUniforms,
      vertexShader: SIM_VERTEX,
      fragmentShader: displayFragment(this._layout, this._raw),
      depthTest: false, depthWrite: false
    });
    this._quad = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), this._displayMaterial);
    this._quad.frustumCulled = false;
    this._quad.renderOrder = -1000;
    // The only legal place a visualizer touches the renderer.
    this._quad.onBeforeRender = function (renderer) { self._run(renderer); };
    scene.add(this._quad);
  }

  // --- the map -----------------------------------------------------------

  // Where on the list the track "wants" to be right now. Calm at both ends,
  // turbulent in the middle - which is also roughly how the list is ordered,
  // so progress maps onto it directly.
  _targetIndex(progress, dynamics) {
    const arc = Math.sin(clamp01(progress) * Math.PI);          // 0 -> 1 -> 0
    const span = 1 + (OPERATING_POINTS.length - 2) * arc;
    // dynamics is the AMPLITUDE of the walk: a quiet track stays near the calm
    // end of wherever the arc puts it, a dynamic one uses the whole span.
    const reach = 0.35 + 0.65 * clamp01(dynamics);
    const jitter = (Math.random() * 2 - 1) * reach * 2.0;
    return Math.max(0, Math.min(OPERATING_POINTS.length - 1,
                                Math.round(span * reach + jitter)));
  }

  _stepTowards(target, beatSeconds) {
    if (target === this._index) return;
    // ONE RUNG AT A TIME. The list is ordered so neighbours are neighbours on
    // the Pearson map; two rungs in one move can cross a dead band, and a dead
    // band is not recoverable.
    const next = this._index + Math.sign(target - this._index);
    this._fromIndex = this._index;
    this._toIndex = next;
    this._index = next;
    this._transT = 0;
    this._transDur = Math.max(0.4, TRANSITION_BEATS * beatSeconds);
  }

  _forceConservative(beatSeconds) {
    if (this._index === CONSERVATIVE_INDEX) return;
    this._stepTowards(CONSERVATIVE_INDEX, beatSeconds);
  }

  // --- seeding and injection ---------------------------------------------

  _requestSeed() { this._seeded = false; }

  _queueSpot(x, y, radius) {
    if (this._pendingSpots.length >= INJECT_SLOTS) return;
    this._pendingSpots.push({ x: x, y: y, r: radius });
  }

  // The emptiest cell of the last reduction, which is exactly "the centre of an
  // empty zone" and costs nothing: the map is already in hand for the safety
  // checks. Up to a second stale, which at these timescales is nothing.
  _emptiestCell() {
    let best = 0, bestV = Infinity;
    for (let i = 0; i < this._reduceMap.length; i++) {
      if (this._reduceMap[i] < bestV) { bestV = this._reduceMap[i]; best = i; }
    }
    const cx = best % REDUCE_N, cy = Math.floor(best / REDUCE_N);
    const jitter = 0.6 / REDUCE_N;
    return {
      x: (cx + 0.5) / REDUCE_N + (Math.random() * 2 - 1) * jitter,
      y: (cy + 0.5) / REDUCE_N + (Math.random() * 2 - 1) * jitter
    };
  }

  // --- the frame ----------------------------------------------------------

  update(deltaTime, profile) {
    const dt = Math.min(deltaTime, 0.1);
    this._paused = profile.playbackState === 'PAUSED';
    if (!this._paused) this._time += dt;

    const director = profile.director || {};
    const bands = director.bands || { low: 0.5, mid: 0.5, high: 0.5 };
    const character = director.character || {};
    const bpm = (profile.trackFeatures && profile.trackFeatures.bpm) || DEFAULT_BPM;
    const beatSeconds = 60 / Math.max(40, bpm);

    // --- palette
    const palette = profile.artworkPalette;
    if (palette && palette !== this._lastPaletteRef) {
      this._lastPaletteRef = palette;
      const vivid = palette.vividColors && palette.vividColors.length
        ? palette.vividColors : [0x3a7fb5, 0xf0d9a0];
      this._displayUniforms.uLow.value.set(vivid[0]);
      this._displayUniforms.uHigh.value.set(vivid[vivid.length > 1 ? 1 : 0]);
      // Deep, never black: a true black background makes the fronts read as
      // cut-out shapes rather than as something suspended in a medium.
      const dom = palette.dominantColors && palette.dominantColors.length
        ? palette.dominantColors[0] : 0x101828;
      this._displayUniforms.uBack.value.set(dom).multiplyScalar(0.22);
    }

    // --- artwork, and a reseed whenever the track changes
    if (profile.artworkUrl !== this._lastArtworkUrl) {
      this._lastArtworkUrl = profile.artworkUrl;
      if (profile.artworkUrl) this._loadArtwork(profile.artworkUrl);
      else { this._hasArtwork = false; this._seedUniforms.uHasArtwork.value = 0; }
    }
    if (profile.trackId !== this._lastTrackId) {
      this._lastTrackId = profile.trackId;
      this._seedUniforms.uSeedPhase.value = Math.random() * 100;
      this._requestSeed();
    }
    if (this._forceSeed) { this._forceSeed = false; this._requestSeed(); }

    // --- the walk, on phrases
    const phrase = profile.phraseIndex;
    if (phrase != null && this._lastPhraseIndex != null && phrase !== this._lastPhraseIndex
        && this._forcedFK == null && this._time - this._lastStepAt > MIN_DWELL) {
      const target = this._targetIndex(profile.progress || 0,
                                       character.dynamics != null ? character.dynamics : 0.5);
      this._stepTowards(target, beatSeconds);
      this._lastStepAt = this._time;
    }
    this._lastPhraseIndex = phrase;

    // --- f and k. NEVER a cut: a step change in k can extinguish a pattern in
    // a handful of iterations, and the crossing is the most interesting thing
    // this piece does anyway - spots stretching into stripes, a labyrinth
    // breaking into dots.
    if (this._forcedFK) {
      this._simUniforms.uF.value = this._forcedFK.f;
      this._simUniforms.uK.value = this._forcedFK.k;
    } else {
      if (this._transT < 1) this._transT = Math.min(1, this._transT + dt / this._transDur);
      const a = OPERATING_POINTS[this._fromIndex];
      const b = OPERATING_POINTS[this._toIndex];
      const s = this._transT * this._transT * (3 - 2 * this._transT);
      const f = a.f + (b.f - a.f) * s;
      this._simUniforms.uF.value = f;
      // Clamped to the live band's lower edge - see K_FLOOR. Both endpoints are
      // above it by construction, so this only bends the middle of a path.
      this._simUniforms.uK.value = Math.max(a.k + (b.k - a.k) * s, kFloor(f));
    }

    // --- iterations from bands.mid
    const L = this._layout;
    this._iterations = L.itersMin === L.itersMax ? L.itersMin
      : Math.round(L.itersMin + (L.itersMax - L.itersMin) * clamp01(bands.mid));

    // --- bands.high sharpens the fronts
    this._displayUniforms.uEdgeGain.value = EDGE_GAIN_BASE + EDGE_GAIN_BAND * clamp01(bands.high);

    // --- a rise in bands.low drops a spot into the emptiest place
    const nowMs = this._time * 1000;
    const low = clamp01(bands.low);
    if (low - this._lastLowBand > LOW_RISE && nowMs - this._lastInjectAt > INJECT_MIN_MS
        && this._seeded && !this._paused) {
      const cell = this._emptiestCell();
      this._queueSpot(cell.x, cell.y, INJECT_RADIUS);
      this._lastInjectAt = nowMs;
    }
    this._lastLowBand = low;

    // --- the window. Zoom and drift on incommensurate periods, so the pair
    // never repeats within a track.
    const z = 0.5 - 0.5 * Math.cos(this._time * (2 * Math.PI / ZOOM_PERIOD));
    this._displayUniforms.uWindowZoom.value = ZOOM_MIN + (ZOOM_MAX - ZOOM_MIN) * z;
    const d = this._time * (2 * Math.PI / DRIFT_PERIOD);
    this._displayUniforms.uWindowCentre.value.set(
      0.5 + 0.30 * Math.cos(d), 0.5 + 0.30 * Math.sin(d * 0.77));
  }

  // Everything that touches the renderer, in the one place allowed to.
  _run(renderer) {
    const prevTarget = renderer.getRenderTarget();

    if (this._state.ensureSize(renderer)) this._seeded = false;
    this._reduce1.ensureSize(renderer);
    this._reduce2.ensureSize(renderer);

    if (!this._seeded) {
      this._seed(renderer);
      this._seeded = true;
    }

    if (!this._paused) {
      // Spots queued this frame ride on the FIRST iteration only; after that
      // the slots are switched off so the spot is injected once, not once per
      // iteration.
      const spots = this._simUniforms.uInject.value;
      for (let i = 0; i < INJECT_SLOTS; i++) {
        const s = this._pendingSpots[i];
        if (s) spots[i].set(s.x, s.y, s.r, 1);
        else spots[i].set(0, 0, 0, 0);
      }
      this._pendingSpots.length = 0;

      this._simQuad.material = this._simMaterial;
      for (let i = 0; i < this._iterations; i++) {
        this._simUniforms.uState.value = this._state.read.texture;
        renderer.setRenderTarget(this._state.write);
        renderer.render(this._simScene, this._ortho);
        this._state.swap();
        if (i === 0) for (let j = 0; j < INJECT_SLOTS; j++) spots[j].w = 0;
      }

      this._frame++;
      if (this._frame % CHECK_FRAMES === 0) this._check(renderer);
    }

    this._displayUniforms.uState.value = this._state.read.texture;
    renderer.setRenderTarget(prevTarget);
  }

  _seed(renderer) {
    this._simQuad.material = this._seedMaterial;
    // BOTH targets: the first simulation pass reads one and writes the other,
    // and a half-seeded pair shows one frame of black before it settles.
    for (const rt of [this._state.read, this._state.write]) {
      renderer.setRenderTarget(rt);
      renderer.render(this._simScene, this._ortho);
    }
  }

  // The two failures that a reaction-diffusion piece cannot detect from its own
  // parameters, measured from the field itself.
  _check(renderer) {
    this._simQuad.material = this._reduce1Material;
    this._reduce1Uniforms.uSrc.value = this._state.read.texture;
    renderer.setRenderTarget(this._reduce1.write);
    renderer.render(this._simScene, this._ortho);

    this._simQuad.material = this._reduce2Material;
    this._reduce2Uniforms.uSrc.value = this._reduce1.read.texture;
    renderer.setRenderTarget(this._reduce2.write);
    renderer.render(this._simScene, this._ortho);

    // One readback a second. It stalls the pipeline, which is why it is not
    // done per frame - but it is the only way to know what the field actually
    // contains, and guessing from f and k is exactly how a piece ends up
    // showing a blank screen for twenty minutes.
    renderer.readRenderTargetPixels(this._reduce2.read, 0, 0, REDUCE_N, REDUCE_N, this._readBuf);
    let sum = 0, cov = 0, sq = 0;
    for (let i = 0; i < REDUCE_N * REDUCE_N; i++) {
      const v = this._readBuf[i * 4];
      this._reduceMap[i] = v;
      sum += v;
      cov += this._readBuf[i * 4 + 1];
      sq += this._readBuf[i * 4 + 2];
    }
    const cells = REDUCE_N * REDUCE_N;
    this._meanB = sum / cells;
    this._coverage = cov / cells;
    // RMS contrast: the field's standard deviation over its mean. A pattern of
    // any density has structure and scores well above zero; a flat wash scores
    // nothing, whatever its level.
    const variance = Math.max(0, sq / cells - this._meanB * this._meanB);
    this._contrast = this._meanB > 1e-6 ? Math.sqrt(variance) / this._meanB : 0;

    const bpm = DEFAULT_BPM;
    const beatSeconds = 60 / bpm;

    this._lowStreak = this._meanB < EXTINCT_FRACTION ? this._lowStreak + 1 : 0;

    if (this._lowStreak >= EXTINCT_CONFIRMATIONS) {
      this._lowStreak = 0;
      this._extinctions++;
      const count = RESEED_SPOTS_MIN
        + Math.floor(Math.random() * (RESEED_SPOTS_MAX - RESEED_SPOTS_MIN + 1));
      for (let i = 0; i < count; i++) {
        this._queueSpot(Math.random(), Math.random(), INJECT_RADIUS * 1.5);
      }
      console.warn('[turing] extinction confirmee: B moyen ' + this._meanB.toFixed(5)
        + ' sous ' + EXTINCT_FRACTION + ' deux fois de suite, au point "'
        + OPERATING_POINTS[this._index].name
        + '" (f=' + this._simUniforms.uF.value.toFixed(3)
        + ' k=' + this._simUniforms.uK.value.toFixed(3) + '), '
        + count + ' taches reinjectees (total ' + this._extinctions + ')');
    } else if (this._coverage > SATURATED_FRACTION && this._contrast < SATURATED_CONTRAST) {
      this._saturations++;
      console.warn('[turing] saturation: B couvre ' + (this._coverage * 100).toFixed(1)
        + '% avec un contraste de ' + this._contrast.toFixed(4)
        + ' (champ uni) au point "' + OPERATING_POINTS[this._index].name
        + '", repli vers "' + OPERATING_POINTS[CONSERVATIVE_INDEX].name
        + '" (total ' + this._saturations + ')');
      this._forceConservative(beatSeconds);
    }

    if (this._stats) {
      console.log('[turing] B moyen ' + this._meanB.toFixed(4)
        + ' couverture ' + (this._coverage * 100).toFixed(1) + '%'
        + ' contraste ' + this._contrast.toFixed(3)
        + ' point "' + OPERATING_POINTS[this._index].name + '"'
        + ' f=' + this._simUniforms.uF.value.toFixed(4)
        + ' k=' + this._simUniforms.uK.value.toFixed(4)
        + ' iter=' + this._iterations);
    }
  }

  _loadArtwork(url) {
    loadArtworkTexture(
      url,
      // No mipmaps and clamp: the seed pass samples it once per grid texel at
      // roughly 1:1, and a mipmapped read would blur away exactly the small
      // bright details that make one cover's seed different from another's.
      { wrap: THREE.ClampToEdgeWrapping, mipmaps: false },
      (texture) => {
        const previous = this._artworkTexture;
        this._artworkTexture = texture;
        this._seedUniforms.uArtwork.value = texture;
        this._seedUniforms.uHasArtwork.value = 1;
        this._hasArtwork = true;
        if (previous) previous.dispose();
        // The cover arrives after the first frames, so seed again with it.
        this._requestSeed();
      },
      () => {
        this._hasArtwork = false;
        this._seedUniforms.uHasArtwork.value = 0;
      }
    );
  }

  dispose() {
    if (this._quad) {
      this._quad.onBeforeRender = null;
      if (this._scene) this._scene.remove(this._quad);
      this._quad.geometry.dispose();
    }
    if (this._simQuad) this._simQuad.geometry.dispose();
    for (const m of [this._displayMaterial, this._simMaterial, this._seedMaterial,
                     this._reduce1Material, this._reduce2Material]) {
      if (m) m.dispose();
    }
    for (const b of [this._state, this._reduce1, this._reduce2]) if (b) b.dispose();
    if (this._artworkTexture) { this._artworkTexture.dispose(); this._artworkTexture = null; }
    this._quad = null;
    this._simQuad = null;
  }
}
